import React, { useState, useEffect, useMemo } from "react";
import Select from "react-select";
import "bootstrap/dist/css/bootstrap.min.css";
import {
  useTable,
  usePagination,
  useSortBy,
  useGlobalFilter,
} from "react-table";
import apiClient from "../utils/apiClient";
import { SERVER_BASE } from "../config/server";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faEllipsisV,
  faPencil,
  faPlus,
  faCancel,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import Swal from "sweetalert2";

/* -------------------- GLOBAL SEARCH -------------------- */
const GlobalFilter = ({ filter, setFilter }) => (
  <input
    value={filter || ""}
    onChange={(e) => setFilter(e.target.value)}
    placeholder="Search..."
    className="form-control"
  />
);

/* -------------------- TABLE -------------------- */
const TableComponent = ({
  columns,
  data,
  globalFilter,
  pageSize,
}) => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    state,
    previousPage,
    nextPage,
    setGlobalFilter,
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const { pageIndex } = state;

  useEffect(() => {
    setGlobalFilter(globalFilter);
  }, [globalFilter]);

  return (
    <>
      <table className="table table-striped" {...getTableProps()} style={{ fontSize: "13px" }}
      >
        <thead>
          {headerGroups.map((hg) => (
            <tr {...hg.getHeaderGroupProps()}>
              {hg.headers.map((col) => (
                <th {...col.getHeaderProps(col.getSortByToggleProps())} style={{
                  cursor: "pointer",
                  width: col.width,
                  minWidth: col.minWidth,
                  maxWidth: col.maxWidth,
                }}>
                  {col.render("Header")}
                  {col.isSorted ? (col.isSortedDesc ? " 🔽" : " 🔼") : ""}
                </th>
              ))}
            </tr>
          ))}
        </thead>

        <tbody {...getTableBodyProps()}>
          {page.map((row) => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()}>
                {row.cells.map((cell) => (
                  <td
                    {...cell.getCellProps()}
                    style={{
                      color:
                        row.original.status === "Pending"
                          ? "#0d6efd"
                          : row.original.status === "Approved"
                            ? "#198754"
                            : "inherit",
                    }}
                  >
                    {cell.render("Cell")}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="d-flex justify-content-between">
        <button
          className="btn btn-secondary"
          disabled={!canPreviousPage}
          onClick={previousPage}
        >
          Previous
        </button>

        <span>
          Page <strong>{pageIndex + 1}</strong> of {pageOptions.length}
        </span>

        <button
          className="btn btn-secondary"
          disabled={!canNextPage}
          onClick={nextPage}
        >
          Next
        </button>
      </div>
    </>
  );
};

/* -------------------- MAIN COMPONENT -------------------- */
const Deductions = ({ accessOfDepartments, permissions }) => {
  const navigate = useNavigate();
  const name = localStorage.getItem("name");
  const pin = localStorage.getItem("pin");
  const code = localStorage.getItem("code");

  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [statusFilter, setStatusFilter] = useState("all");
  const [departmentFilter, setDepartmentFilter] = useState(null);
  const [globalFilter, setGlobalFilter] = useState("");
  const [pageSize, setPageSize] = useState(25);

  /* -------------------- LOAD DATA -------------------- */
  const loadData = async () => {
    setLoading(true);
    try {
      if (statusFilter === "pending") {
        const { data } = await apiClient.get(
          SERVER_BASE + "/getPendingLessDrafts",
          { params: { code, accessOfDepartments } }
        );
        setTableData(data);
      } else if (statusFilter === "approved") {
        const { data } = await apiClient.get(
          SERVER_BASE + "/getApprovedLessDrafts",
          { params: { code, accessOfDepartments } }
        );
        setTableData(data);
      } else {
        const [p, a] = await Promise.all([
          apiClient.get(SERVER_BASE + "/getPendingLessDrafts", {
            params: { code, accessOfDepartments },
          }),
          apiClient.get(SERVER_BASE + "/getApprovedLessDrafts", {
            params: { code, accessOfDepartments },
          }),
        ]);
        setTableData([...p.data, ...a.data]);
      }
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadData();
  }, [statusFilter]);

  /* -------------------- DEPARTMENT OPTIONS -------------------- */
  const departmentOptions = useMemo(() => {
    const deps = [
      ...new Set(tableData.map((d) => d.department).filter(Boolean)),
    ];
    return [
      { value: "all", label: "All Departments" },
      ...deps.map((d) => ({ value: d, label: d })),
    ];
  }, [tableData]);

  /* -------------------- FILTERED DATA -------------------- */
  const filteredData = useMemo(() => {
    if (!departmentFilter || departmentFilter.value === "all") {
      return tableData;
    }
    return tableData.filter(
      (row) => row.department === departmentFilter.value
    );
  }, [tableData, departmentFilter]);

  /* -------------------- STATUS OPTIONS -------------------- */
  const statusOptions = [
    { value: "pending", label: "Pending" },
    { value: "approved", label: "Approved" },
    { value: "all", label: "All" },
  ];
  const handleCancel = async (rowData) => {
    Swal.fire({
      title: "Confirm Cancellation",
      text: "Enter your PIN to cancel this voucher. This action cannot be undone.",
      icon: "warning",
      input: "password",
      inputPlaceholder: "Enter PIN",
      inputAttributes: {
        maxlength: 6,
        autocapitalize: "off",
        autocorrect: "off",
      },
      showCancelButton: true,
      confirmButtonText: "Cancel Voucher",
      preConfirm: async (enteredPin) => {
        if (!enteredPin) {
          Swal.showValidationMessage("PIN is required");
          return false;
        }

        if (enteredPin !== pin) {
          Swal.showValidationMessage("Invalid PIN");
          return false;
        }

        try {
          await apiClient.post(
            `${SERVER_BASE}/cancelInvnetoryDraft/${rowData.id}`,
            {
              cancelledBy: name, // ✅ Name added
              type: 'Inventory'
            }
          );

          return true; // ✅ tells Swal it's successful
        } catch (err) {
          Swal.showValidationMessage("Failed to cancel voucher");
          return false;
        }
      },
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          icon: "success",
          title: "Cancelled",
          text: "Voucher cancelled successfully.",
        });

        loadData();
      }
    });
  };
  /* -------------------- COLUMNS -------------------- */
  const columns = useMemo(
    () => [
      { Header: "S.No", Cell: ({ row }) => row.index + 1 },
      {
        Header: "Date",
        accessor: "date",
        Cell: ({ value }) => value && moment(value).format("DD-MMM-YY"),
      },
      { Header: "Ref No.", accessor: "draftCode" },
      { Header: "Department", accessor: "department" },
      { Header: "Event", accessor: "purpose" },
      {
        Header: "Inventory Data",
        width: 230,
        minWidth: 230,
        maxWidth: 230,
        accessor: "inventory_data",
        Cell: ({ value }) => {
          try {
            const parsed =
              typeof value === "string" ? JSON.parse(value) : value;
            return parsed?.map((i) => i.item).join(", ") || "-";
          } catch {
            return "-";
          }
        },
      },

      { Header: "Status", accessor: "status" },
      {
        Header: "Preview", accessor: "Preview", Cell: ({ row }) => {
          const r = row.original;
          return (
            permissions?.read &&
            <button
              className="btn btn-sm btn-outline-success"
              onClick={() =>
                navigate("../../previewInventory", {
                  state: { id: r.id },
                })
              }
            >
              View
            </button>

          )
        }
      },

      {
        Header: "#",
        width: 30,
        minWidth: 30,
        maxWidth: 30,
        Cell: ({ row }) => {
          const r = row.original;

          return (
            <div className="dropdown">
              <button
                className="btn btn-light"
                data-bs-toggle="dropdown"
              >
                <FontAwesomeIcon icon={faEllipsisV} />
              </button>

              <ul className="dropdown-menu dropdown-menu-end">
                {/* Preview */}
                {/* <li>
                  <button
                    className="dropdown-item"
                    onClick={() =>
                      navigate("../../previewInventory", {
                        state: { id: r.id },
                      })
                    }
                  >
                    <FontAwesomeIcon icon={faEye} /> Preview
                  </button>
                </li> */}

                {/* Edit only in Pending */}
                {r.status === "Pending" && permissions?.edit && (
                  <li>
                    <button
                      className="dropdown-item"
                      onClick={() =>
                        navigate("../lessInventory", {
                          state: { rowId: r.id },
                        })
                      }
                    >
                      <FontAwesomeIcon icon={faPencil} /> Edit
                    </button>
                  </li>

                )}
                {permissions?.delete &&
                  r.status == "Pending"
                  && (
                    <li>
                      <button
                        className="dropdown-item"
                        onClick={() =>
                          handleCancel(r)
                        }
                      >
                        <FontAwesomeIcon icon={faCancel} />{" "}
                        Cancel
                      </button>
                    </li>
                  )}
                <li>
                  <hr />
                </li>
                <li>
                  {r.createdBy && (
                    <li className="dropdown-item">
                      <FontAwesomeIcon icon={faPlus} /> {r.createdBy}
                      {r.created_at &&
                        ` (${moment(r.created_at).format("DD-MMM-YY hh:mm A")})`}
                    </li>
                  )}
                </li>
                <li>
                  {r.updatedBy && (
                    <li className="dropdown-item">
                      <FontAwesomeIcon icon={faPencil} /> {r.updatedBy}
                      {r.updatedAt &&
                        ` (${moment(r.updatedAt).format("DD-MMM-YY hh:mm A")})`}
                    </li>
                  )}
                </li>
                <li>
                  {r.approvedBy && (
                    <li className="dropdown-item">
                      <FontAwesomeIcon icon={faCheckCircle
                      } /> {r.approvedBy}
                      {r.approvedAt &&
                        ` (${moment(r.approvedAt).format("DD-MMM-YY hh:mm A")})`}
                    </li>
                  )}
                </li>
              </ul>
            </div>
          );
        },
      },
    ],
    [permissions]
  );

  return (
    <div className="formcontainer mt-5">
      <h4>Deductions</h4>

      <div className="row mb-3 align-items-center">
        {permissions?.write && (
          <div className="col-md-2 mb-2">
            <button
              className="btn btn-outline-success w-100"
              onClick={() => navigate("../lessInventory")}
            >
              Less Inventory
            </button>
          </div>
        )}

        <div className="col-md-3 mb-2">
          <GlobalFilter filter={globalFilter} setFilter={setGlobalFilter} />
        </div>

        <div className="col-md-3 mb-2">
          <Select
            options={departmentOptions}
            value={departmentFilter}
            onChange={setDepartmentFilter}
            placeholder="Department"
            isClearable
          />
        </div>

        <div className="col-md-2 mb-2">
          <Select
            options={statusOptions}
            value={statusOptions.find((s) => s.value === statusFilter)}
            onChange={(v) => setStatusFilter(v.value)}
          />
        </div>

        <div className="col-md-2 mb-2">
          <select
            className="form-select"
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
          >
            {[25, 50, 100, 200].map((s) => (
              <option key={s} value={s}>
                Show {s}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <TableComponent
          columns={columns}
          data={filteredData}
          globalFilter={globalFilter}
          pageSize={pageSize}
        />
      )}
    </div>
  );
};

export default Deductions;
